import { CompleteKundali, GunaScore, GunMilanResult } from '../types/kundali';
import { getSavBySign, getSavByBhava } from './vedicEngine';
import { calculateAshtakootaSynastry } from './v518/ashtakootaEngine';

export function calculateGunMilan(boy: CompleteKundali, girl: CompleteKundali): GunMilanResult {
  const ashtakoota = calculateAshtakootaSynastry(
    boy,
    girl,
    boy.birthDetails?.name || 'Groom',
    girl.birthDetails?.name || 'Bride',
    'en'
  );

  const getComp = (name: string) =>
    ashtakoota.components.find((c) => c.gunaName.toLowerCase().includes(name.toLowerCase())) ||
    ashtakoota.components[0];

  const cVarna = getComp('Varna');
  const cVashya = getComp('Vashya');
  const cTara = getComp('Tara');
  const cYoni = getComp('Yoni');
  const cMaitri = getComp('Maitri');
  const cGana = getComp('Gana');
  const cBhakoot = getComp('Bhakoot');
  const cNadi = getComp('Nadi');

  const varnaGuna: GunaScore = {
    name: 'Varna',
    boyAttribute: cVarna.partnerAValue,
    girlAttribute: cVarna.partnerBValue,
    obtainedPoints: cVarna.obtainedPoints,
    maxPoints: 1,
    description: cVarna.descriptionEn,
    status: cVarna.obtainedPoints === 1 ? 'Excellent' : 'Poor'
  };

  const vashyaGuna: GunaScore = {
    name: 'Vashya',
    boyAttribute: cVashya.partnerAValue,
    girlAttribute: cVashya.partnerBValue,
    obtainedPoints: cVashya.obtainedPoints,
    maxPoints: 2,
    description: cVashya.descriptionEn,
    status: cVashya.obtainedPoints === 2 ? 'Excellent' : cVashya.obtainedPoints >= 1 ? 'Average' : 'Poor'
  };

  const taraGuna: GunaScore = {
    name: 'Tara',
    boyAttribute: cTara.partnerAValue,
    girlAttribute: cTara.partnerBValue,
    obtainedPoints: cTara.obtainedPoints,
    maxPoints: 3,
    description: cTara.descriptionEn,
    status: cTara.obtainedPoints >= 2.5 ? 'Excellent' : cTara.obtainedPoints >= 1.5 ? 'Good' : 'Poor'
  };

  const yoniGuna: GunaScore = {
    name: 'Yoni',
    boyAttribute: cYoni.partnerAValue,
    girlAttribute: cYoni.partnerBValue,
    obtainedPoints: cYoni.obtainedPoints,
    maxPoints: 4,
    description: cYoni.descriptionEn,
    status: cYoni.obtainedPoints >= 3 ? 'Excellent' : cYoni.obtainedPoints >= 2 ? 'Average' : 'Poor'
  };

  const maitriGuna: GunaScore = {
    name: 'Maitri',
    boyAttribute: cMaitri.partnerAValue,
    girlAttribute: cMaitri.partnerBValue,
    obtainedPoints: cMaitri.obtainedPoints,
    maxPoints: 5,
    description: cMaitri.descriptionEn,
    status: cMaitri.obtainedPoints >= 4 ? 'Excellent' : cMaitri.obtainedPoints >= 2.5 ? 'Average' : 'Poor'
  };

  const ganaGuna: GunaScore = {
    name: 'Gana',
    boyAttribute: cGana.partnerAValue,
    girlAttribute: cGana.partnerBValue,
    obtainedPoints: cGana.obtainedPoints,
    maxPoints: 6,
    description: cGana.descriptionEn,
    status: cGana.obtainedPoints >= 5 ? 'Excellent' : cGana.obtainedPoints >= 3 ? 'Average' : 'Poor'
  };

  const bhakootGuna: GunaScore = {
    name: 'Bhakoot',
    boyAttribute: cBhakoot.partnerAValue,
    girlAttribute: cBhakoot.partnerBValue,
    obtainedPoints: cBhakoot.obtainedPoints,
    maxPoints: 7,
    description: cBhakoot.descriptionEn,
    status: cBhakoot.obtainedPoints === 7 ? 'Excellent' : 'Poor'
  };

  const nadiGuna: GunaScore = {
    name: 'Nadi',
    boyAttribute: cNadi.partnerAValue,
    girlAttribute: cNadi.partnerBValue,
    obtainedPoints: cNadi.obtainedPoints,
    maxPoints: 8,
    description: cNadi.descriptionEn,
    status: cNadi.obtainedPoints === 8 ? 'Excellent' : 'Poor'
  };

  const gunas: GunaScore[] = [
    varnaGuna,
    vashyaGuna,
    taraGuna,
    yoniGuna,
    maitriGuna,
    ganaGuna,
    bhakootGuna,
    nadiGuna
  ];

  const totalScore = ashtakoota.totalScore;

  // Manglik compatibility
  const boyManglik = ashtakoota.manglikAnalysis.partnerA.isManglik;
  const girlManglik = ashtakoota.manglikAnalysis.partnerB.isManglik;
  const isManglikCompatible =
    ashtakoota.manglikAnalysis.mutualManglikCompatibility === 'COMPATIBLE' ||
    ashtakoota.manglikAnalysis.mutualManglikCompatibility === 'BALANCED';

  // Ashtakvarga Compatibility Check
  const boyMoon = boy.planets.find((p) => p.name === 'Moon') || boy.planets[0];
  const girlMoon = girl.planets.find((p) => p.name === 'Moon') || girl.planets[0];
  const boyMoonSignIdx = boyMoon.signIndex;
  const girlMoonSignIdx = girlMoon.signIndex;

  const boyMoonSignBindusInGirlSAV = getSavBySign(girl.ashtakvarga, boyMoonSignIdx);
  const girlMoonSignBindusInBoySAV = getSavBySign(boy.ashtakvarga, girlMoonSignIdx);

  const boy7thHouseBindus = getSavByBhava(boy.ashtakvarga, 7, boy.houses);
  const girl7thHouseBindus = getSavByBhava(girl.ashtakvarga, 7, girl.houses);

  const totalAVPoints =
    boyMoonSignBindusInGirlSAV + girlMoonSignBindusInBoySAV + boy7thHouseBindus + girl7thHouseBindus;
  const compatibilityPercentage = Math.min(100, Math.round((totalAVPoints / 112) * 100));

  let avVerdict: 'Highly Compatible' | 'Balanced' | 'Needs Planetary Remedial Support' = 'Balanced';
  if (compatibilityPercentage >= 85) avVerdict = 'Highly Compatible';
  else if (compatibilityPercentage < 70) avVerdict = 'Needs Planetary Remedial Support';

  const ashtakvargaMatch = {
    boyMoonSignBindusInGirlSAV,
    girlMoonSignBindusInBoySAV,
    boy7thHouseBindus,
    girl7thHouseBindus,
    compatibilityPercentage,
    verdict: avVerdict,
    summary: `Groom's Moon sign receives ${boyMoonSignBindusInGirlSAV} Bindus in Bride's SAV. Bride's Moon sign receives ${girlMoonSignBindusInBoySAV} Bindus in Groom's SAV. 7th house strength: Groom ${boy7thHouseBindus} Bindus, Bride ${girl7thHouseBindus} Bindus.`
  };

  let verdict: GunMilanResult['verdict'] = 'Not Recommended';
  if (totalScore >= 28) verdict = 'Excellent Match';
  else if (totalScore >= 21) verdict = 'Good Match';
  else if (totalScore >= 18) verdict = 'Average Match';

  let summary = `Total Ashtakoot score is ${totalScore} out of 36 (${compatibilityPercentage}% Ashtakvarga harmony). `;
  if (totalScore < 18) {
    summary += 'The total score is below 18 Gunas. Vedic astrological remedies and detailed chart consultation are recommended before proceeding.';
  } else if (cNadi.isDoshaPresent) {
    summary += 'Nadi Dosha is present, which requires Mahamrityunjaya Japa and Sankalpa remedies.';
  } else if (!isManglikCompatible) {
    summary += 'One partner is Manglik while the other is non-Manglik. Performing Kuja Dosha Shanti Puja is advised.';
  } else {
    summary += 'This match shows strong emotional, physical, Ashtakvarga, and cosmic harmony for a blessed marital life.';
  }

  return {
    boyName: boy.birthDetails?.name || 'Groom',
    girlName: girl.birthDetails?.name || 'Bride',
    totalScore,
    maxScore: 36,
    gunas,
    manglikMatch: {
      boyManglik,
      girlManglik,
      isCompatible: isManglikCompatible,
      note: ashtakoota.manglikAnalysis.mutualVerdictEn
    },
    ashtakvargaMatch,
    verdict,
    summary
  };
}
